/**
 * The app-level saved-views seam.
 *
 * A table cannot build its own saved-views backing: the source comes from the
 * app's `ApiClient` and the identity from the live session. Declared here once,
 * `<AddressTable enableSavedViews>` and `<TransactionTable enableSavedViews>` are
 * booleans instead of six pages each assembling a source, an identity, a menu and
 * a save dialog — which is why the capability shipped unreachable.
 *
 * Session-scoped on purpose: the identity keys the stored views, so it belongs
 * below `<TenantProvider>` where a tenant switch re-keys it.
 */
import * as React from "react";
import { SavedViewsProvider, type SharePrincipalOption } from "@authe/saved-views";
import { usePermissions } from "@authe/ui-core/permissions";
import { useSavedViewsSource } from "@/api/adapters";
import { RESOURCES } from "@/config";
import { useMyTeams } from "./useMyTeams";
import { useStorageIdentity } from "./storageIdentity";

export function DashboardSavedViews({
  children,
}: {
  children: React.ReactNode;
}): React.ReactElement {
  const source = useSavedViewsSource();
  const identity = useStorageIdentity();
  return (
    <SavedViewsProvider source={source} identity={identity}>
      {children}
    </SavedViewsProvider>
  );
}

/**
 * What this viewer may do with a saved view, in the vocabulary the affordances
 * use — written down ONCE, here, because every surface that offers saved views
 * asks the same four questions and `saved-views-api` answers them with four
 * different scopes.
 *
 * The mapping, from the route tags and `SavedViewService._guard_scope`:
 *
 * | Affordance                    | Scope                      |
 * |-------------------------------|----------------------------|
 * | Save / rename / delete my own | `saved-views:personalize`  |
 * | Save one others can see       | `saved-views:create`       |
 * | Mint + manage share links     | `saved-views:edit`         |
 * | Delete one others can see     | `saved-views:delete`       |
 *
 * The split exists so every tier can keep a view of its own while "and now
 * everyone in my tenant sees it" stays gated. Ownership (can I touch *this*
 * row?) is ABAC and stays the API's to enforce — the UI cannot know it, and a
 * button that 403s on somebody else's row is a smaller problem than one that is
 * missing on your own.
 */
export interface SavedViewPermissions {
  /** Keep, rename and delete views of your own. */
  canPersonalize: boolean;
  /** Publish a view beyond yourself (the "Shared" scope in the save dialog). */
  canPublish: boolean;
  /** Mint, revoke and edit tokenized share links. */
  canShare: boolean;
  /** Delete a view that is published beyond `user` scope. */
  canDeleteShared: boolean;
}

/**
 * The affordance → scope mapping, as data so it can be checked.
 *
 * A `can()` miss is a DENIAL, not an error: name a verb the catalogue does not
 * carry and the button silently disappears for everyone, Admin included. Naming
 * them here (rather than inline in the hook) is what lets a test assert they are
 * real catalogue verbs without rendering anything.
 */
export const SAVED_VIEW_SCOPES = {
  canPersonalize: "personalize",
  canPublish: "create",
  canShare: "edit",
  canDeleteShared: "delete",
} as const satisfies Record<keyof SavedViewPermissions, string>;

export function useSavedViewPermissions(): SavedViewPermissions {
  const { can } = usePermissions();
  return {
    canPersonalize: can(RESOURCES.savedViews, SAVED_VIEW_SCOPES.canPersonalize),
    canPublish: can(RESOURCES.savedViews, SAVED_VIEW_SCOPES.canPublish),
    canShare: can(RESOURCES.savedViews, SAVED_VIEW_SCOPES.canShare),
    canDeleteShared: can(RESOURCES.savedViews, SAVED_VIEW_SCOPES.canDeleteShared),
  };
}

/**
 * The caller's teams, in the shape the share dialog's audience picker takes.
 *
 * One hook rather than the same three-line rename beside every `ShareViewDialog`
 * — a `team`-target link is gated on membership of the ids it carries, so
 * getting them from anywhere but the caller's real memberships mints a link
 * nobody can open.
 */
export function useShareTeamOptions(): SharePrincipalOption[] {
  const teams = useMyTeams();
  return React.useMemo(
    () => teams.map((team) => ({ id: team.id, label: team.name })),
    [teams],
  );
}

/**
 * Can a LIVE (dynamic) share actually be served to whoever opens it?
 *
 * A saved view now stores a re-fetch seed wherever a surface can name what its
 * graph is (see `SceneGraphView`'s `seed`), so the recipe exists — but a seed is
 * only half of it. Replaying one needs a `GraphSeedResolver` inside the viewer
 * SPA that opens the link, and a transaction's graph lives in a cold S3 body
 * reached through the signed-cookie CDN scope, which those pages have no session
 * for. Until they do, a live link would resolve to an explanation instead of a
 * graph.
 *
 * So the option stays off — and off HERE, once, rather than by leaving the seed
 * unwritten in each surface. The seed is still worth storing: it is what a
 * "refresh this snapshot" reload replays, and it is the half that has to exist
 * before the other half can be built. Flip this to `true` in the same change
 * that gives `share-transactions-graph-{public,private}-page` a resolver.
 */
export const LIVE_SHARES_SERVABLE = false;
